import React, { useEffect, useMemo, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useNavigate } from "react-router";
import {
  ALL_FORM_LIST_LIMIT,
  formTemplateStatusDefault,
  formTemplateStatusOptions,
  LOADER_STATUS,
  SORT_TYPE,
} from "Constants/constant";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import SelectOption from "components/common/Select/Select";
import { roleAccess, platformRoleAccess } from "helpers/RolesPermission";
import FormsListActionButtons from "./FormsListActionButtons";
import {
  getFormRowStatusBadge,
  getPlatformFormsMeta,
  isNativePlatformAdminView,
} from "./platformAdminForms.utils";

const PlatformAdminFormsList = ({
  templateList,
  editFormTemplateHandler,
  viewFormTemplateHandler,
  isLoading,
  queryParams,
  formCategoriesList,
  handleFetch,
  getSortBy,
  paginationObj,
  setPaginationObj,
  routeRole,
  isUserGracePeriodOver,
  resetAndFetch,
}) => {
  const navigate = useNavigate();
  const meta = useMemo(() => getPlatformFormsMeta(routeRole), [routeRole]);
  const canCreate = isNativePlatformAdminView()
    ? platformRoleAccess("platform.catalog.forms")
    : roleAccess([], meta.permissionName, meta.createPermission);

  const [searchInput, setSearchInput] = useState(queryParams.search || "");
  const [sortBy, setSortBy] = useState(queryParams.sort_field);
  const [selectedFormCategories, setSelectedFormCategories] = useState([]);
  const [sortDescending, setSortDescending] = useState(
    queryParams.sort_order === SORT_TYPE.DESC
  );
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.NA);
  const [formTemplateStatus, setFormTemplateStatus] = useState(
    formTemplateStatusDefault
  );
  const initialFetchDoneRef = useRef(false);

  useEffect(() => {
    getSortBy(sortBy);
  }, [getSortBy, sortBy]);

  useEffect(() => {
    setSortBy(queryParams.sort_field ? queryParams.sort_field : null);
    if (queryParams.sort_order) {
      setSortDescending(queryParams.sort_order === SORT_TYPE.DESC);
    }
    if (queryParams.hasOwnProperty("filter") && queryParams.filter !== null) {
      const categories = queryParams.filter.form_category;
      setSelectedFormCategories(
        Array.isArray(categories) && categories.length > 0 ? categories : []
      );
    }
    if (queryParams.search) {
      setSearchInput(queryParams.search);
    }
  }, [queryParams]);

  useEffect(() => {
    if (!queryParams.status) {
      return;
    }
    const nextStatus = formTemplateStatusOptions.find(
      (option) => option.value === queryParams.status
    );
    if (!nextStatus) {
      return;
    }
    setFormTemplateStatus((current) =>
      current?.value === nextStatus.value ? current : nextStatus
    );
  }, [queryParams.status]);

  useEffect(() => {
    setRenderLoading(LOADER_STATUS.PENDING);
    const isInitialFetch = !initialFetchDoneRef.current;
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        const payload = {
          status: formTemplateStatus.value,
          search: searchInput,
          page: 1,
        };

        if (isInitialFetch) {
          payload.form_category = [];
        } else if (selectedFormCategories.length > 0) {
          payload.form_category = selectedFormCategories;
        }

        handleFetch(payload, sortBy);
        initialFetchDoneRef.current = true;
      }
    }, isInitialFetch ? 0 : 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    if (!isLoading && renderLoading === LOADER_STATUS.PENDING) {
      setRenderLoading(LOADER_STATUS.COMPLATE);
    }
  }, [isLoading, renderLoading]);

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(!sortDescending);
    handleFetch({
      status: formTemplateStatus.value,
      sort_field: by,
      sort_order: !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  const sortedTemplates = useMemo(
    () =>
      [...(templateList || [])].sort((left, right) =>
        (left.title || "").localeCompare(right.title || "")
      ),
    [templateList]
  );

  const columns = useMemo(
    () => [
      {
        key: "title",
        header: "Title",
        sortable: true,
        render: (row) => <span className="font-medium text-gray-900">{row.title}</span>,
      },
      {
        key: "category_name",
        header: "Category",
        render: (row) => row.category_name || "—",
      },
      {
        key: "sub_category_name",
        header: "Subcategory",
        render: (row) => row.sub_category_name || "—",
      },
      {
        key: "status",
        header: "Status",
        render: (row) => {
          const badge = getFormRowStatusBadge(row, formTemplateStatus?.value);

          return <PaStatusBadge label={badge.label} variant={badge.variant} />;
        },
      },
      {
        key: "actions",
        header: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => (
          <FormsListActionButtons
            editFormTemplateHandler={editFormTemplateHandler}
            viewFormTemplateHandler={viewFormTemplateHandler}
            formTemplateStatus={formTemplateStatus}
            template={row}
            routeRole={routeRole}
            isUserGracePeriodOver={isUserGracePeriodOver}
            handleFetch={handleFetch}
            selectedFormCategories={selectedFormCategories}
          />
        ),
      },
    ],
    [
      editFormTemplateHandler,
      formTemplateStatus,
      handleFetch,
      isUserGracePeriodOver,
      routeRole,
      selectedFormCategories,
      viewFormTemplateHandler,
    ]
  );

  const loading = isLoading || renderLoading !== LOADER_STATUS.COMPLATE;

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        actions={
          canCreate ? (
            <button
              type="button"
              onClick={() => navigate(meta.createPath)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <BiPlus className="h-4 w-4" aria-hidden="true" />
              {meta.createLabel}
            </button>
          ) : null
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
        <SelectOption
          cssClass="z-20"
          isDisabled={loading}
          isSearch={false}
          selectedOption={selectedFormCategories}
          handleChange={(options) => {
            const nextSelection = Array.isArray(options) ? options : [];
            handleFetch(
              {
                status: formTemplateStatus.value,
                form_category: nextSelection,
                page: 1,
              },
              sortBy
            );
            setSelectedFormCategories(
              nextSelection.map((option) => ({
                value: option.id,
                label: option.label ?? option.name,
                id: option.id,
              }))
            );
          }}
          data={formCategoriesList}
          isMulti
        />
        <SelectOption
          isDisabled={loading}
          isSearch={false}
          selectedOption={formTemplateStatus}
          handleChange={(option) => {
            setFormTemplateStatus(option);
            resetAndFetch({ status: option.value });
          }}
          data={formTemplateStatusOptions}
          cssClass="z-20"
        />
      </div>

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search form templates"
        resultCount={paginationObj.total}
      />

      {loading ? (
        <PaSkeleton variant="table" lines={6} />
      ) : sortedTemplates.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <PaEmptyState
            title="No form templates"
            message="Nothing matched your filters"
            description={
              canCreate
                ? "Create a new platform form template to get started."
                : undefined
            }
            action={
              canCreate ? (
                <button
                  type="button"
                  onClick={() => navigate(meta.createPath)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {meta.createLabel}
                </button>
              ) : null
            }
          />
        </div>
      ) : (
        <PaDataTable
          columns={columns}
          rows={sortedTemplates}
          rowKey="id"
          sortKey={sortBy}
          sortDirection={sortDescending ? "desc" : "asc"}
          onSort={(key) => flipSort(key)}
        />
      )}

      {!loading && sortedTemplates.length > 0 && paginationObj.total > 0 ? (
        <PaPagination
          page={paginationObj.page}
          totalPages={paginationObj.totalPages || 0}
          total={paginationObj.total}
          rows={sortedTemplates}
          perPage={ALL_FORM_LIST_LIMIT}
          loading={loading}
          onPageChange={(page) => {
            const next = { ...paginationObj, page };
            setPaginationObj(next);
            handleFetch({ page, status: formTemplateStatus.value }, sortBy);
          }}
        />
      ) : null}
    </PlatformAdminLayout>
  );
};

export default PlatformAdminFormsList;
